import { PRIORITY_LABEL, type Column, type Priority } from '../lib/types.ts'
import styles from './primitives.module.css'

/**
 * Primitives from docs/ui-components.md. Every visual value lives in primitives.module.css over
 * tokens; nothing here hard-codes a colour or a size (ADR 0009).
 */

export function PriorityMeter({ priority }: { priority: Priority }): React.JSX.Element {
  return (
    <span
      className={`${styles.meter} ${styles[`p${priority}`]}`}
      // Colour is never the only carrier of meaning (ui-spec §11).
      aria-label={`Priorytet: ${PRIORITY_LABEL[priority]}`}
      title={`Priorytet: ${PRIORITY_LABEL[priority]}`}
    >
      <span className={`${styles.bar} ${styles.bar1}`} />
      <span className={`${styles.bar} ${styles.bar2}`} />
      <span className={`${styles.bar} ${styles.bar3}`} />
    </span>
  )
}

/**
 * Always a span. `interactive` only adds the caret and the pointer cursor — the clickable element
 * is whatever wraps the pill (the cell menu's trigger). A button here would nest inside that one,
 * which is invalid HTML and breaks React.
 */
export function StatusPill({
  column,
  interactive = false,
}: {
  column: Column
  interactive?: boolean
}): React.JSX.Element {
  return (
    <span
      className={`${styles.pill} ${styles[column.color]}${interactive ? ` ${styles.pillInteractive}` : ''}`}
    >
      <span className={`${styles.pillDot} ${styles[`${column.color}Dot`]}`} />
      {column.name}
      {interactive && <span className={styles.caret}>▾</span>}
    </span>
  )
}

/**
 * `onRemove` is in ui-components.md and was never implemented, which is why labels could be read
 * everywhere and edited nowhere — the API accepted them, the UI had no way to send them.
 */
export function LabelChip({
  children,
  onRemove,
}: {
  children: string
  onRemove?: () => void
}): React.JSX.Element {
  if (!onRemove) return <span className={styles.label}>{children}</span>
  return (
    <span className={`${styles.label} ${styles.labelRemovable}`}>
      {children}
      <button
        type="button"
        className={styles.labelRemove}
        aria-label={`Usuń labelkę ${children}`}
        onClick={onRemove}
      >
        ×
      </button>
    </span>
  )
}

/** Overdue is readable without reading (ui-spec §5.2): colour plus an explicit title. */
export function DueDate({ date, long = false }: { date: string | null; long?: boolean }) {
  if (!date) return <span className={`${styles.due} ${styles.dueEmpty}`}>—</span>

  const days = daysUntil(date)
  const tone = days < 0 ? styles.dueOverdue : days <= 1 ? styles.dueSoon : ''
  const [year, month, day] = date.split('-')

  let text = long ? `${day}.${month}.${year}` : `${day}.${month}`
  if (long && days === 0) text += ' · dziś'
  else if (long && days === 1) text += ' · jutro'

  return (
    <span
      className={`${styles.due} ${tone}`}
      title={days < 0 ? `Po terminie o ${Math.abs(days)} dni` : `Termin: ${day}.${month}.${year}`}
    >
      {text}
    </span>
  )
}

/**
 * A date that carries no urgency — when something was created, as opposed to when it is due.
 *
 * Deliberately not DueDate with the colours switched off: a due date answers "how much time is
 * left" and earns colour, a creation date answers "how old is this" and must never compete with it
 * for attention. Same format, quieter tone, always the muted colour.
 */
export function DateCell({ date, long = false }: { date: string | null; long?: boolean }) {
  if (!date) return <span className={`${styles.due} ${styles.dueEmpty}`}>—</span>

  const iso = date.slice(0, 10)
  const [year, month, day] = iso.split('-')
  const days = -daysUntil(iso)

  let text = long ? `${day}.${month}.${year}` : `${day}.${month}`
  if (long && days === 0) text += ' · dziś'
  else if (long && days === 1) text += ' · wczoraj'

  return (
    <span className={`${styles.due} ${styles.dueNeutral}`} title={`Dodano ${day}.${month}.${year}`}>
      {text}
    </span>
  )
}

function daysUntil(isoDate: string): number {
  // Destructured with defaults rather than `!`: noUncheckedIndexedAccess is on precisely so a
  // malformed date reaches us as undefined instead of pretending to be a number.
  const [y = 0, m = 1, d = 1] = isoDate.split('-').map(Number)
  const due = new Date(y, m - 1, d)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((due.getTime() - today.getTime()) / 86_400_000)
}

/** Renders nothing at zero — the count only appears once a discussion exists (PRD §4.2). */
export function CommentCount({ count }: { count: number }): React.JSX.Element | null {
  if (count <= 0) return null
  return (
    <span className={styles.comments} title={`${count} komentarzy`}>
      <span className={styles.bubble} />
      <span className={styles.commentsCount}>{count}</span>
    </span>
  )
}

export function Kbd({ children }: { children: string }): React.JSX.Element {
  return <kbd className={styles.kbd}>{children}</kbd>
}

export function EmptyState({
  text,
  action,
  variant = 'dashed',
}: {
  text: string
  action?: { label: string; onClick: () => void }
  variant?: 'dashed' | 'plain'
}): React.JSX.Element {
  return (
    <div className={`${styles.empty}${variant === 'dashed' ? ` ${styles.emptyDashed}` : ''}`}>
      <span className={styles.emptyText}>{text}</span>
      {action && (
        <button type="button" className={styles.emptyAction} onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  )
}

/**
 * Query failure, next to the thing that failed (ui-spec §7).
 *
 * The default wording follows the spec's rule: what happened and what to do, no apology. It exists
 * because every view here used to treat "failed" as "empty" — a network error and an empty database
 * rendered the same sentence, so the app quietly claimed there was nothing to show.
 */
export function ErrorState({
  text = 'Nie udało się wczytać danych.',
  onRetry,
  retryLabel = 'Spróbuj ponownie',
}: {
  text?: string
  onRetry?: () => void
  retryLabel?: string
}): React.JSX.Element {
  return (
    <div className={styles.error} role="alert">
      <span className={styles.errorText}>{text}</span>
      {onRetry && (
        <button type="button" className={styles.errorAction} onClick={onRetry}>
          {retryLabel}
        </button>
      )}
    </div>
  )
}

export function Skeleton({
  height = 58,
  delay = 0,
}: {
  height?: number
  delay?: 0 | 200 | 400
}): React.JSX.Element {
  return <div className={styles.skeleton} style={{ height, animationDelay: `${delay}ms` }} />
}
